import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Rocket, Check, ArrowRight, Zap, Palette, ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { TrustedMarquee } from "@/components/TrustedMarquee";
import { CursorGlow } from "@/components/CursorGlow";
import portfolioSpeakingAuthor from "@/assets/portfolio/speaking-author-sharp.jpg";
import portfolioSeniorTransportation from "@/assets/portfolio/senior-transportation.png";
import portfolioCatering from "@/assets/portfolio/catering.png";
import portfolioDogGrooming from "@/assets/portfolio/dog-grooming.png";
import portfolioFinancialAdvisor from "@/assets/portfolio/financial-advisor.png";
import portfolioPhysicalTherapist from "@/assets/portfolio/physical-therapist.png";
import portfolioMedSpa from "@/assets/portfolio/medspa.png";
import portfolioCoffeeShop from "@/assets/portfolio/coffee-shop.png";
import portfolioLandscaping from "@/assets/portfolio/landscaping.png";
import portfolioAccounting from "@/assets/portfolio/accounting.png";
import portfolioKeynotes from "@/assets/portfolio/keynotes.png";
import portfolioHairSalon from "@/assets/portfolio/hair-salon.png";
import avatarElaine from "@/assets/testimonials/avatar-elaine-B3rNOmMI.png";
import avatarTarrn from "@/assets/testimonials/avatar-tarrn-DBz18DZ_.png";
import avatarLissa from "@/assets/testimonials/avatar-lissa-B28uOaHv.png";
import avatarAhriana from "@/assets/testimonials/avatar-ahriana-BmR4_NC6.png";
import avatarCarrie from "@/assets/testimonials/avatar-carrie-DtFoRfBZ.png";

const bookingUrl = "/book";

const PAGE_TITLE = "Best Coaching Websites | Portfolio of Coach Website Designs";
const PAGE_DESC = "See the best coaching websites designed by a dedicated coach website designer & life coach website designer. Real portfolio of custom coaching websites launched in 3 days.";
const PAGE_URL = "https://threedaylaunch.com/portfolio";

export const Route = createFileRoute("/portfolio")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      { name: "keywords", content: "best coaching websites, coaching websites, coach website designer, life coach website designer, coaching website design, coach website examples" },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:url", content: PAGE_URL },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: PAGE_TITLE,
          url: PAGE_URL,
          description: PAGE_DESC,
          about: ["coaching websites", "coach website designer", "life coach website designer"],
          publisher: { "@type": "Organization", name: "Three Day Launch" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://threedaylaunch.com/" },
            { "@type": "ListItem", position: 2, name: "Portfolio", item: PAGE_URL },
          ],
        }),
      },
    ],
  }),
  component: PortfolioPage,
});

const projects = [
  { img: portfolioSpeakingAuthor, name: "Author & Speaker Coaching Website", tag: "Keynote-driven coach site", title: "Author and speaker coaching website design example" },
  { img: portfolioKeynotes, name: "Keynote Speaker Coaching Website", tag: "Booking inquiries", title: "Keynote speaker coaching website design example" },
  { img: portfolioFinancialAdvisor, name: "Financial Coaching Website", tag: "Lead capture funnel", title: "Financial coach website design example" },
  { img: portfolioPhysicalTherapist, name: "Wellness Coaching Website", tag: "Lead capture", title: "Wellness and physical therapy coaching website example" },
  { img: portfolioMedSpa, name: "Beauty & Confidence Coaching Website", tag: "Booking-driven", title: "Beauty and confidence coaching website design example" },
  { img: portfolioAccounting, name: "Business Coaching Website", tag: "Advisory firm", title: "Business and advisory coaching website design example" },
  { img: portfolioCatering, name: "Hospitality Coaching Website", tag: "Event-focused", title: "Hospitality coaching website design example" },
  { img: portfolioCoffeeShop, name: "Local Brand Coaching Website", tag: "Community-first", title: "Local brand coaching website design example" },
  { img: portfolioLandscaping, name: "Outdoor Coaching Website", tag: "Quote requests", title: "Outdoor and landscaping coaching website example" },
  { img: portfolioDogGrooming, name: "Pet & Lifestyle Coaching Website", tag: "Booking-driven", title: "Pet and lifestyle coaching website design example" },
  { img: portfolioHairSalon, name: "Style & Image Coaching Website", tag: "Booking-driven", title: "Style and image coaching website design example" },
  { img: portfolioSeniorTransportation, name: "Service-Based Coaching Website", tag: "Trust-first site", title: "Service-based coaching website design example" },
];

function PortfolioPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <div className="relative overflow-hidden bg-background">
        <CursorGlow opacity={0.22} />
        <PortfolioHero />
        <TrustedMarquee />
      </div>
      <BestCoachingWebsites />
      <WhyHire />
      <HowAffordable />
      <Process />
      <ThenWhat />
      <Testimonials />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
}

function Header() {
  return (
    <header className="absolute top-0 left-0 right-0 z-50">
      <div className="mx-auto max-w-7xl px-6 py-5 flex flex-wrap items-center justify-between gap-3">
        <Link to="/" className="flex w-full sm:w-auto items-center gap-2 text-foreground">
          <div className="h-9 w-9 rounded-lg bg-primary-gradient flex items-center justify-center shadow-glow">
            <Rocket className="h-5 w-5 text-white" />
          </div>
          <span className="font-bold tracking-tight text-lg">Three Day Launch<sup className="text-xs ml-0.5">®</sup></span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-foreground/80">
          <Link to="/" className="hover:text-primary uppercase">HOME</Link>
          <Link to="/blog" className="hover:text-primary uppercase">BLOG</Link>
          <Link to="/about" className="hover:text-primary uppercase">ABOUT</Link>
          <Link to="/contact" className="hover:text-primary uppercase">CONTACT</Link>
        </nav>
        <Button asChild className="ml-auto bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold rounded-full px-5">
          <a href={bookingUrl}>Launch a Coaching Website You Love</a>
        </Button>
      </div>
    </header>
  );
}

function PortfolioHero() {
  return (
    <section className="relative pt-32 pb-10 md:pt-40 md:pb-12 bg-background">
      <div className="mx-auto max-w-4xl px-6 text-center">
        <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary">
          Coaching Website Portfolio
        </div>
        <h1 className="mt-4 text-4xl md:text-6xl font-bold leading-[1.1]">
          The <span className="text-primary">best coaching websites</span>, designed in 3 days.
        </h1>
        <p className="mt-5 text-lg text-muted-foreground">
          The coach website designer & life coach website designer portfolio of
          custom coaching websites that attract leads and convert clients.
        </p>
        <div className="mt-8 flex justify-center">
          <a
            href="/book"
            className="inline-flex items-center justify-center rounded-full bg-primary-gradient text-primary-foreground font-semibold h-12 px-6 shadow-glow"
          >
            Get Started With A Free Call
          </a>
        </div>
      </div>
    </section>
  );
}

function BestCoachingWebsites() {
  const [showAllProjects, setShowAllProjects] = useState(false);
  return (
    <section id="jealous" className="py-24 bg-secondary text-secondary-foreground scroll-mt-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/15 border border-primary/30 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary">
            See What's Possible
          </div>
          <h2 className="mt-4 text-4xl md:text-5xl text-white font-bold">
            Your old coaching website is <span className="text-primary">already jealous.</span>
          </h2>
          <p className="mt-4 text-white/70">
            Real examples of the best coaching websites we've designed for coaches, consultants &
            experts across every niche.
          </p>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {projects.map((p, i) => (
            <figure key={p.name} className={`group ${i >= 4 && !showAllProjects ? "hidden md:block" : ""}`}>
              <div className="relative">
                <div className="relative bg-[#1a1a1a] rounded-t-2xl p-2 pt-6 border border-white/10">
                  <div className="flex gap-1.5 absolute top-2.5 left-4">
                    <span className="h-2 w-2 rounded-full bg-red-400/80" />
                    <span className="h-2 w-2 rounded-full bg-yellow-400/80" />
                    <span className="h-2 w-2 rounded-full bg-green-400/80" />
                  </div>
                  <div className="portfolio-scroll relative h-[280px] overflow-hidden rounded-lg bg-white">
                    <img
                      src={p.img}
                      alt={`${p.name} — custom coaching website design by Three Day Launch`}
                      title={p.title}
                      width={1280}
                      height={1920}
                      loading="lazy"
                      className="portfolio-scroll-img w-full block"
                    />
                  </div>
                </div>
                <div className="mx-auto h-3 max-w-full bg-gradient-to-b from-[#2a2a2a] to-[#0f0f0f] rounded-b-2xl" style={{ width: "108%" }} />
                <div className="mx-auto h-1 bg-black/60 rounded-b-full" style={{ width: "30%" }} />
              </div>
              <figcaption className="mt-4 text-center">
                <div className="text-white font-semibold">{p.name}</div>
                <div className="text-white/60 text-xs uppercase tracking-wider mt-1">{p.tag}</div>
              </figcaption>
            </figure>
          ))}
        </div>
        {!showAllProjects && projects.length > 4 && (
          <div className="mt-8 text-center md:hidden">
            <Button variant="link" onClick={() => setShowAllProjects(true)} className="text-secondary-foreground underline underline-offset-4 p-0 h-auto text-sm font-medium">
              Load More Designs
            </Button>
          </div>
        )}

        <div className="mt-14 text-center">
          <Button asChild size="lg" className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center">
            <a href={bookingUrl}>Launch a Coaching Website You Love</a>
          </Button>
        </div>
      </div>
    </section>
  );
}

function WhyHire() {
  return (
    <section className="relative overflow-hidden py-[45px]">
      <div className="mx-auto max-w-7xl px-6">
        <div className="relative rounded-3xl bg-hero-gradient text-white p-10 md:p-16 overflow-hidden shadow-card-soft">
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-primary/30 blur-3xl" />
          <div className="absolute -left-20 -bottom-20 h-72 w-72 rounded-full bg-highlight/20 blur-3xl" />

          <div className="relative grid md:grid-cols-[1.4fr_1fr] gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-primary/15 border border-primary/30 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary">
                Coach Website Designer
              </div>
              <h2 className="mt-4 text-3xl md:text-5xl font-bold leading-tight">
                <span className="text-primary">Attract</span> leads & <span className="text-primary">convert</span> clients
              </h2>
              <p className="mt-5 text-white/75 text-lg max-w-xl">
                The best coaching websites do more than look pretty — they bring you the right
                clients on autopilot. That's what we build.
              </p>
              <div className="mt-8 grid sm:grid-cols-3 gap-4">
                {[
                  { icon: Zap, label: "Live in 3 days" },
                  { icon: Palette, label: "100% custom design" },
                  { icon: ShieldCheck, label: "Strategic, multi-page sites" },
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                    <Icon className="h-5 w-5 text-primary" />
                    <span className="text-sm font-semibold">{label}</span>
                  </div>
                ))}
              </div>
              <Button asChild size="lg" className="mt-8 bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center">
                <a href={bookingUrl}>Start Today <ArrowRight className="ml-2 h-4 w-4" /></a>
              </Button>
            </div>

            <div className="relative">
              <div className="bg-white text-foreground rounded-3xl p-8 pb-12 shadow-2xl rotate-2">
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">THE SIGNATURE COACH</div>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="text-5xl font-bold text-primary">3 Day Launch®</span>
                </div>
                <div className="mt-4 h-px bg-border" />
                <ul className="mt-4 space-y-2 text-sm">
                  {["Mobile Responsive Website", "SSL Certificate (HTTPS)", "SEO (Search Engine Optimization)", "Launched In 3 Days", "Ongoing Support"].map(i => (
                    <li key={i} className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> {i}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HowAffordable() {
  return (
    <section id="how" className="py-24 bg-background">
      <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary">
            Best Coaching Websites, Without The Big Agency Price
          </div>
          <h2 className="mt-4 text-2xl md:text-3xl font-bold leading-tight">
            The other guys want you to pick between speed and strategy. <span className="text-foreground">We'd rather give you both.</span>
          </h2>
          <div className="mt-8 space-y-4">
            {[
              { t: "Lightning Fast Timelines", d: "Days instead of months, so you can start seeing ROI from your new coaching website asap." },
              { t: "Custom Quality You Can Trust", d: "Every pixel designed for your coaching business. Never a template." },
            ].map(({ t, d }) => (
              <div key={t} className="flex gap-4">
                <div className="h-10 w-10 rounded-xl bg-primary-gradient flex items-center justify-center flex-shrink-0 shadow-glow">
                  <Check className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="font-bold">{t}</div>
                  <div className="text-muted-foreground text-sm">{d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative">
          <div className="bg-secondary text-secondary-foreground rounded-3xl p-10 shadow-card-soft">
            <div className="text-xs font-bold uppercase tracking-wider text-white/80">The typical agency experience</div>
            <div className="mt-2 text-5xl font-bold line-through text-white/70">3–6 Months</div>
            <div className="mt-1 text-sm text-white/60">Endless revisions. Cookie-cutter template. You're just another ticket in the queue.</div>
            <div className="mt-8 h-px bg-white/10" />
            <div className="mt-6 text-xs font-bold uppercase tracking-wider text-primary">THE THREE DAY LAUNCH WAY</div>
            <div className="mt-2 text-4xl md:text-5xl font-bold text-primary whitespace-nowrap">Live in 3 Days</div>
            <div className="mt-1 text-sm text-white/70">A multi-page, custom website designed just for your business. Never a template.</div>
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-6 mt-12 text-center">
        <Button asChild size="lg" className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center">
          <a href={bookingUrl}>Get A Website That WORKS</a>
        </Button>
      </div>
    </section>
  );
}

function Process() {
  const steps = [
    { n: "01", t: "Meet over Zoom for your Strategy Call.", d: "One on one with your coach website designer to get clear on your vision and website goals." },
    { n: "02", t: "We design your custom coaching website, all in one day.", d: "No meetings this day, you'll just need to be available to provide feedback notes." },
    { n: "03", t: "Domain connection & launch celebration.", d: "We hit publish and connect your domain (like yourcoaching.com) and you're officially online!" },
  ];
  return (
    <section id="process" className="pt-12 pb-24 md:py-24 bg-muted/40">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-sm font-semibold uppercase tracking-widest text-primary mb-3">Our Trademarked Process</div>
          <h2 className="text-4xl md:text-5xl font-bold">Three days, <span className="text-primary">and you're live</span></h2>
          <p className="mt-4 text-muted-foreground text-lg">From "let's do it" to "we're live" in less time than most designers take to send a proposal.</p>
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {steps.map((s) => (
            <div key={s.n} className="bg-card rounded-3xl p-8 border border-border shadow-card-soft">
              <div className="text-6xl font-bold text-primary/50">{s.n}</div>
              <h3 className="mt-2 text-xl font-bold">{s.t}</h3>
              <p className="mt-3 text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 text-center">
          <Button asChild size="lg" className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center">
            <a href={bookingUrl}>Get A Website That WORKS</a>
          </Button>
        </div>
      </div>
    </section>
  );
}

function ThenWhat() {
  return (
    <section className="relative py-24 overflow-hidden bg-secondary text-secondary-foreground">
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-60"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 20%, color-mix(in oklab, var(--primary) 35%, transparent) 0%, transparent 45%), radial-gradient(circle at 85% 80%, color-mix(in oklab, var(--primary) 25%, transparent) 0%, transparent 50%)",
        }}
      />
      <div aria-hidden="true" className="absolute -top-32 -left-32 h-80 w-80 rounded-full bg-primary/30 blur-3xl" />
      <div aria-hidden="true" className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-primary/20 blur-3xl" />

      <div className="relative mx-auto max-w-3xl px-6 text-center">
        <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/15 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary">
          we've got your back
        </div>
        <h2 className="mt-5 text-4xl md:text-5xl font-bold tracking-tight text-white">
          Three days for your coaching website build... <span className="text-primary">then what?</span>
        </h2>
        <div className="mt-8 space-y-5 text-lg text-white/75">
          <p>
            We don't just build you a beautiful coaching website and then ditch you to "figure it out"
            post launch. After your website is complete, we'll be only a message away should you need
            any updates or help.
          </p>
          <p>Your coaching website should evolve with your brand as you continue to scale!</p>
        </div>
      </div>
    </section>
  );
}

const testimonials = [
  { quote: "Always pleasant, upbeat, and open to feedback, making the process smooth and enjoyable. Your creativity and attention to detail really shone through, and you were able to translate my ideas into a beautiful, functional website.", name: "Elaine E.", avatar: avatarElaine },
  { quote: "You taught me more than I had previously learned from paying two other business coaches. And the experience of working together was fun!", name: "Tarrn M.", avatar: avatarTarrn },
  { quote: "Absolutely amazing. Sierra really cares about her clients and helps them achieve their goals. You will not regret working with her.", name: "Lissa A.", avatar: avatarLissa },
  { quote: "Now, this website feels like ME — Like people can actually feel who I am and see what I'm up to! YOU TOTALLY ROCK!", name: "Ahriana P.", avatar: avatarAhriana },
  { quote: "I love how you asked great questions and had creative ways of figuring out what we wanted. The end result really shows that part of us. You helped us send the message we were hoping to send.", name: "Carrie S.", avatar: avatarCarrie },
];

function Testimonials() {
  const loop = [...testimonials, ...testimonials];
  return (
    <section id="testimonials" className="py-24 bg-background overflow-hidden scroll-mt-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold">Don't take our word for it,</h2>
          <p className="mt-2 text-4xl md:text-5xl font-bold text-primary">take theirs:</p>
        </div>
      </div>
      <div className="group relative w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
        <div className="flex w-max gap-6 animate-marquee group-hover:[animation-play-state:paused]">
          {loop.map((t, i) => (
            <figure key={`${t.name}-${i}`} className="w-[340px] md:w-[400px] shrink-0 rounded-2xl bg-card border border-border p-8 flex flex-col shadow-sm">
              <div className="text-primary text-8xl leading-none font-black mb-2" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>“</div>
              <blockquote className="text-foreground/80 leading-relaxed flex-1">{t.quote}</blockquote>
              <figcaption className="mt-6 flex items-center gap-3">
                <img
                  src={t.avatar}
                  alt={`${t.name} — coaching website client testimonial headshot`}
                  title={`${t.name}, Three Day Launch coaching website client`}
                  className="h-12 w-12 rounded-full object-cover"
                  loading="lazy"
                />
                <div className="font-semibold">{t.name}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-6 mt-16 text-center">
        <Button asChild size="lg" className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center">
          <a href={bookingUrl}>Start Today</a>
        </Button>
      </div>
    </section>
  );
}

function FAQ() {
  const items = [
    {
      q: "What makes the best coaching websites stand out?",
      a: "The best coaching websites combine a clear value proposition, strong calls to action, fast load times, mobile-first design, and SEO foundations so the right clients can find you. Every coaching website we design is built around your specific offer and audience — never a template.",
    },
    {
      q: "Are you a life coach website designer specifically?",
      a: "Yes — we design coaching websites for life coaches, business coaches, health & wellness coaches, mindset coaches, relationship coaches, and more. Our life coach website designer process focuses on translating your voice, story, and methodology into a site that converts visitors into discovery calls.",
    },
    {
      q: "How long does it take to launch a coaching website?",
      a: "Three days. Day one is your kickoff Zoom call. Day two is design day with feedback rounds. Day three we connect your domain and set up hosting so your coaching website goes live.",
    },
    {
      q: "How much does it cost to hire a coach website designer?",
      a: "Most full websites with us land between $1,000 and $4,000 USD. Pricing is based on number of pages and content needs. Payment plans are available.",
    },
    {
      q: "Will my coaching website be SEO optimized?",
      a: "Yes. Every coaching website we design includes on-page SEO, semantic HTML, fast load times, mobile-responsive layouts, image alt text, and metadata so search engines can rank you for terms your future clients actually search.",
    },
  ];
  return (
    <section id="faq" className="bg-background py-[90px] pt-[45px]">
      <div className="mx-auto max-w-3xl px-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary">FAQ</div>
          <h2 className="mt-4 text-4xl md:text-5xl font-bold">Coaching website questions, answered.</h2>
        </div>
        <Accordion type="single" collapsible className="mt-10 space-y-3">
          {items.map((item, i) => (
            <AccordionItem key={i} value={`i-${i}`} className="bg-card rounded-2xl border border-border px-6 data-[state=open]:shadow-card-soft">
              <AccordionTrigger className="text-left font-semibold text-base hover:no-underline">{item.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground whitespace-pre-line">{item.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="mt-12 text-center">
          <Button asChild size="lg" className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center">
            <a href={bookingUrl}>Start Today <ArrowRight className="ml-2 h-4 w-4" /></a>
          </Button>
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section id="contact" className="relative overflow-hidden py-24 bg-secondary text-secondary-foreground">
      {/* Layered background */}
      <div className="absolute inset-0 bg-hero-gradient opacity-90" />
      <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-primary/40 blur-3xl animate-pulse" />
      <div className="absolute -bottom-32 -right-32 h-[28rem] w-[28rem] rounded-full bg-highlight/30 blur-3xl animate-pulse" style={{ animationDelay: "1.5s" }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />
      <div
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(ellipse at center, black 40%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 40%, transparent 75%)",
        }}
      />

      <div className="relative mx-auto max-w-5xl px-6 text-center">
        <h2 className="text-4xl md:text-6xl font-bold">
          Your coaching website. <span className="text-primary">Live in 3 days.</span>
        </h2>
        <p className="mt-5 text-white/75 text-lg max-w-xl mx-auto">
          Let's build a website that grows your business.
          <br /><br />
          Get started by scheduling a free, no-pressure call.
        </p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Button asChild size="lg" className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center">
            <a href={bookingUrl}>Start Today <ArrowRight className="ml-2 h-4 w-4" /></a>
          </Button>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-secondary text-secondary-foreground border-t border-white/10 py-10">
      <div className="mx-auto max-w-7xl px-6 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-primary-gradient flex items-center justify-center">
            <Rocket className="h-4 w-4 text-white" />
          </div>
          <span className="font-bold">Three Day Launch<sup className="text-[0.6em] ml-0.5">®</sup></span>
        </div>
        <nav className="flex items-center gap-6 text-sm text-white/70">
          <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
          <Link to="/about" className="hover:text-white transition-colors">About</Link>
          <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
          <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
          <Link to="/terms-and-conditions" className="hover:text-white transition-colors">Terms &amp; Conditions</Link>
        </nav>
        <div className="text-sm text-white/80">© {new Date().getFullYear()} Three Day Launch. Custom Websites.</div>
      </div>
      <div className="mx-auto max-w-7xl px-6 mt-4 text-center text-sm text-white/80">
        Based in Chicago | Serving Clients Worldwide
      </div>
      <div className="mx-auto max-w-7xl px-6 mt-2 text-center text-xs text-white/60">
        Three Day Launch is an independent website design studio creating professional websites.
      </div>
    </footer>
  );
}
